import { useMemo } from 'react'
import {
  findDefaultFulfillment,
  formatFulfillmentWindow
} from 'shop/utils/common'
import { parseTimezoneISO } from 'shop/utils'
import { createCartFulfillmentDate } from 'shop/components/Landing/utils'
import { ASAP_ORDER, PREORDER_ORDER, FulfillmentType } from 'shop/types'
import { ConsumerCart } from 'shop/types/cart'
import {
  OrderFulfillmentType,
  PartnerStore
} from 'shop/components/Landing/types'

interface FulfillmentQueryParams {
  fulfillmentType?: FulfillmentType
  fulfillmentDate?: Date
  fulfillmentTime?: string
}

interface InitialFulfillmentStateParams {
  cart: ConsumerCart | null
  fulfillmentQueryParams: FulfillmentQueryParams
  currentPartnerStore?: PartnerStore | null
}

interface InitialFulfillmentState {
  initialFulfillmentType: string
  cartFulfillmentDate: Date
  defaultFulfillmentDate: Date
  initialOrderDay: string
  initialFulfillmentDate: Date
  initialFulfillmentTime: string | null
  initialFulfillmentTimeRange: string | null
  initialIsEditMode: boolean
  defaultFulfillmentInfo: OrderFulfillmentType | null
}

/** Centralizes all initial fulfillment modal state calculations from cart, query params, and defaults. Calculates values once on mount. Priority order: cart > query params  */
export const useInitialFulfillmentState = ({
  cart,
  fulfillmentQueryParams,
  currentPartnerStore
}: InitialFulfillmentStateParams): InitialFulfillmentState => {
  return useMemo(() => {
    const queryParamFulfillmentType = fulfillmentQueryParams.fulfillmentType
    const defaultFulfillmentInfo =
      findDefaultFulfillment(
        currentPartnerStore?.orderFulfillmentTypes,
        queryParamFulfillmentType
      ) ?? null

    const isCartAsap = cart?.fulfillment.orderType === ASAP_ORDER
    const windowFromDate =
      cart && parseTimezoneISO(cart?.fulfillment.window.from)

    // Calculate initial fulfillment type
    const initialFulfillmentType =
      cart?.fulfillment.type?.toLowerCase() ||
      defaultFulfillmentInfo?.fulfillmentType?.toLowerCase() ||
      ''

    // Calculate fulfillment time ranges
    const defaultFulfillmentTimeRange = defaultFulfillmentInfo?.earliestTimeslot
      ? formatFulfillmentWindow(defaultFulfillmentInfo.earliestTimeslot, 'p')
      : null

    const cartFulfillmentTimeRange = cart?.fulfillment.window
      ? formatFulfillmentWindow(cart?.fulfillment.window, 'p')
      : null

    // Calculate fulfillment dates
    const cartFulfillmentDate = createCartFulfillmentDate(
      isCartAsap ? null : windowFromDate,
      cart?.fulfillment.orderType === PREORDER_ORDER
    )

    const defaultFulfillmentDate = defaultFulfillmentInfo?.earliestTimeslot
      ? parseTimezoneISO(defaultFulfillmentInfo.earliestTimeslot.from)
      : new Date()

    // Calculate fulfillment times
    const defaultFulfillmentTime =
      defaultFulfillmentInfo?.orderType === ASAP_ORDER ? 'immediately' : null

    const cartFulfillmentTime = isCartAsap ? 'immediately' : null

    // Calculate initial order day
    const initialOrderDay = cart?.fulfillment
      ? isCartAsap
        ? 'now'
        : 'scheduled'
      : defaultFulfillmentInfo
        ? defaultFulfillmentInfo?.orderType === ASAP_ORDER
          ? 'now'
          : 'scheduled'
        : 'now'

    // Determine initial fulfillment date based on cart or query params
    const initialFulfillmentDate = cart
      ? cartFulfillmentDate
      : defaultFulfillmentInfo
        ? defaultFulfillmentDate
        : new Date()

    // Determine initial fulfillment time
    const initialFulfillmentTime =
      cartFulfillmentTime || defaultFulfillmentTime || null

    // Determine initial fulfillment time range
    const initialFulfillmentTimeRange =
      cartFulfillmentTimeRange || defaultFulfillmentTimeRange

    // Determine if we're in edit mode
    const initialIsEditMode = cart || defaultFulfillmentInfo ? true : false

    return {
      initialFulfillmentType,
      cartFulfillmentDate,
      defaultFulfillmentDate,
      initialOrderDay,
      initialFulfillmentDate,
      initialFulfillmentTime,
      initialFulfillmentTimeRange,
      initialIsEditMode,
      defaultFulfillmentInfo
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
